import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  BookOpen,
  Building2,
  ChevronRight,
  Check,
  GraduationCap,
  IndianRupee,
  Pencil,
  Search,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS, hasPermission } from '../../constants/permissions';

const money = (value) => Number(value || 0).toLocaleString('en-IN');
const date = (value) => value
  ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';
const statusClass = (value) => {
  const status = String(value || '').toLowerCase();
  if (['completed', 'captured', 'confirmed'].includes(status)) return 'bg-emerald-50 text-emerald-700';
  if (['failed', 'rejected', 'cancelled'].includes(status)) return 'bg-rose-50 text-rose-700';
  return 'bg-amber-50 text-amber-700';
};

export default function CourseManagement() {
  const { user } = useAuth();
  const { courseId } = useParams();
  const navigate = useNavigate();
  const isInstitutionAdmin = user?.role === 'institution_admin';
  const apiBase = isInstitutionAdmin ? '/api/institution-admin' : '/api/admin';
  const routeBase = isInstitutionAdmin ? '/institution' : '/cluster-admin';
  const [courses, setCourses] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedInstitutionId, setSelectedInstitutionId] = useState('');
  const [institutions, setInstitutions] = useState([]);
  const [summary, setSummary] = useState({
    totalCourses: 0,
    totalSeats: 0,
    registeredStudents: 0,
    remainingSeats: 0,
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [editingCourseId, setEditingCourseId] = useState('');
  const [remainingSeatsInput, setRemainingSeatsInput] = useState('');
  const [savingSeats, setSavingSeats] = useState(false);
  const canEditSeats = hasPermission(user, PERMISSIONS.COURSE_MANAGEMENT_EDIT);

  const saveRemainingSeats = async (course) => {
    const remainingSeats = Number(remainingSeatsInput);
    if (!Number.isInteger(remainingSeats) || remainingSeats < 0) {
      toast.error('Remaining seats must be a non-negative whole number');
      return;
    }
    if (remainingSeats > Number(course.totalSeats || 0)) {
      toast.error(`Remaining seats cannot exceed total seats (${course.totalSeats})`);
      return;
    }
    setSavingSeats(true);
    try {
      const response = await axios.patch(`${apiBase}/course-management/${course.id}/seats`, { remainingSeats });
      setCourses((current) => current.map((item) => (
        item.id === course.id ? { ...item, remainingSeats: response.data.remainingSeats } : item
      )));
      setSummary((current) => ({
        ...current,
        remainingSeats: Math.max(0, current.remainingSeats - Number(course.remainingSeats || 0) + Number(response.data.remainingSeats || 0)),
      }));
      setEditingCourseId('');
      toast.success('Remaining seats updated');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Unable to update remaining seats');
    } finally {
      setSavingSeats(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPagination((current) => ({ ...current, page: 1 }));
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    if (!courseId) return () => { active = false; };
    axios.get(`${apiBase}/course-management/${courseId}/students`)
      .then((response) => {
        if (!active) return;
        setDetail(response.data);
      })
      .catch((error) => {
        if (active) toast.error(error.response?.data?.message || 'Unable to load course management');
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [apiBase, courseId]);

  useEffect(() => {
    let active = true;
    if (courseId) return () => { active = false; };
    setTableLoading(true);
    axios.get(`${apiBase}/course-management`, {
      params: {
        page: pagination.page,
        limit: pagination.limit,
        ...(debouncedQuery ? { q: debouncedQuery } : {}),
        ...(!isInstitutionAdmin && selectedInstitutionId ? { institutionId: selectedInstitutionId } : {}),
      },
    })
      .then((response) => {
        if (!active) return;
        setCourses(response.data.items || []);
        setSummary(response.data.stats || {});
        setPagination((current) => ({ ...current, ...(response.data.pagination || {}) }));
        setInstitutions(response.data.institutions || []);
      })
      .catch((error) => {
        if (active) toast.error(error.response?.data?.message || 'Unable to load course management');
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
        setTableLoading(false);
      });
    return () => { active = false; };
  }, [apiBase, courseId, debouncedQuery, isInstitutionAdmin, pagination.page, pagination.limit, selectedInstitutionId]);

  const filtered = courses;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (courseId && detail) {
    const course = detail.course || {};
    const students = detail.students || [];
    return (
      <div className="p-6 lg:p-8 space-y-6">
        <button
          type="button"
          onClick={() => navigate(`${routeBase}/course-management`)}
          className="inline-flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-indigo-600"
        >
          <ArrowLeft size={18} /> Back to courses
        </button>

        <div className="rounded-3xl bg-gradient-to-br from-indigo-600 to-violet-700 p-7 text-white shadow-xl">
          <p className="text-indigo-100 text-sm font-semibold">{course.institutionName}</p>
          <h1 className="text-3xl font-black mt-1">{course.name}</h1>
          <p className="text-indigo-100 mt-2">{course.programmeName}</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-7">
            {[
              ['Total seats', course.totalSeats],
              ['Registered', course.registeredStudents],
              ['Remaining', course.remainingSeats],
              ['Course fee', `₹${money(course.fee)}`],
            ].map(([label, value]) => (
              <div key={label} className="rounded-2xl bg-white/10 border border-white/15 px-4 py-3">
                <p className="text-xs uppercase tracking-wider text-indigo-100 font-bold">{label}</p>
                <p className="text-2xl font-black mt-1">{value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-100 rounded-3xl shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100">
            <h2 className="font-black text-xl text-slate-800">Registered students</h2>
            <p className="text-sm text-slate-500 mt-1">{students.length} active registration{students.length === 1 ? '' : 's'}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-6 py-4">Student</th>
                  <th className="px-6 py-4">Application</th>
                  <th className="px-6 py-4">Contact</th>
                  <th className="px-6 py-4">Admission</th>
                  <th className="px-6 py-4">Payment</th>
                  <th className="px-6 py-4">Registered</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {students.length === 0 ? (
                  <tr><td colSpan="6" className="px-6 py-14 text-center text-slate-500">No students registered for this course.</td></tr>
                ) : students.map((student) => (
                  <tr key={student.applicationId} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <p className="font-bold text-slate-800">{student.name}</p>
                      <p className="text-xs text-slate-500">{student.academicYear || 'Academic year not set'}</p>
                    </td>
                    <td className="px-6 py-4 font-mono text-xs text-indigo-600">{student.applicationId}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      <p>{student.email || '—'}</p>
                      <p>{student.mobile || '—'}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${statusClass(student.admissionStatus)}`}>
                        {student.admissionStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${statusClass(student.paymentStatus)}`}>
                        {student.paymentStatus}
                      </span>
                      <p className="text-xs text-slate-500 mt-1">₹{money(student.amountPaid)}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{date(student.registeredAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  const stats = [
    { label: 'Courses', value: summary.totalCourses, icon: BookOpen, iconClass: 'bg-indigo-50 text-indigo-600' },
    { label: 'Total seats', value: summary.totalSeats, icon: GraduationCap, iconClass: 'bg-blue-50 text-blue-600' },
    { label: 'Registered', value: summary.registeredStudents, icon: Users, iconClass: 'bg-emerald-50 text-emerald-600' },
    { label: 'Remaining', value: summary.remainingSeats, icon: Building2, iconClass: 'bg-amber-50 text-amber-600' },
  ];

  return (
    <div className="space-y-7">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900">Course Management</h1>
          <p className="text-slate-500 mt-1">Monitor course capacity and review registered students.</p>
        </div>
        {!isInstitutionAdmin && (
          <select
            value={selectedInstitutionId}
            onChange={(event) => {
              setSelectedInstitutionId(event.target.value);
              setPagination((current) => ({ ...current, page: 1 }));
            }}
            className="md:w-72 px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:ring-2 focus:ring-indigo-500"
            aria-label="Filter by institution"
          >
            <option value="">All Institutions</option>
            {institutions.map((institution) => (
              <option key={institution.id} value={institution.id}>{institution.name}</option>
            ))}
          </select>
        )}
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {stats.map(({ label, value, icon: Icon, iconClass }) => (
          <div key={label} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex items-center gap-4">
            <div className={`w-11 h-11 rounded-xl ${iconClass} flex items-center justify-center`}>
              <Icon size={21} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider font-bold text-slate-400">{label}</p>
              <p className="text-2xl font-black text-slate-800">{value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-100 rounded-3xl shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex flex-col md:flex-row gap-3">
          <div className="relative max-w-md flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search course or programme"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-6 py-4">Course</th>
                {!isInstitutionAdmin && <th className="px-6 py-4">Institution</th>}
                <th className="px-6 py-4">Fee</th>
                <th className="px-6 py-4">Total seats</th>
                <th className="px-6 py-4">Registered</th>
                <th className="px-6 py-4">Remaining</th>
                <th className="px-6 py-4" aria-label="Open" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tableLoading ? (
                <tr>
                  <td colSpan={isInstitutionAdmin ? 6 : 7} className="px-6 py-14">
                    <div className="flex items-center justify-center gap-3 text-slate-500">
                      <div className="w-6 h-6 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
                      <span className="font-semibold">Loading courses…</span>
                    </div>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={isInstitutionAdmin ? 6 : 7} className="px-6 py-14 text-center text-slate-500">No courses found.</td></tr>
              ) : filtered.map((course) => {
                const filledPercent = course.totalSeats > 0
                  ? Math.min(100, Math.round((course.registeredStudents / course.totalSeats) * 100))
                  : 0;
                return (
                  <tr
                    key={course.id}
                    onClick={() => navigate(`${routeBase}/course-management/${course.id}/students`)}
                    className="hover:bg-indigo-50/40 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 min-w-[240px]">
                      <p className="font-bold text-slate-800">{course.name}</p>
                      <p className="text-xs text-slate-500">{course.programmeName}</p>
                    </td>
                    {!isInstitutionAdmin && <td className="px-6 py-4 text-sm text-slate-600">{course.institutionName}</td>}
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center text-sm font-bold text-slate-700"><IndianRupee size={14} />{money(course.fee)}</span>
                    </td>
                    <td className="px-6 py-4 font-bold text-slate-700">{course.totalSeats}</td>
                    <td className="px-6 py-4 min-w-[150px]">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="font-bold text-indigo-600">{course.registeredStudents}</span>
                        <span className="text-slate-400">{filledPercent}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${filledPercent}%` }} />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {editingCourseId === course.id ? (
                        <div className="flex items-center gap-1" onClick={(event) => event.stopPropagation()}>
                          <input
                            type="number"
                            min="0"
                            max={course.totalSeats}
                            value={remainingSeatsInput}
                            onChange={(event) => setRemainingSeatsInput(event.target.value)}
                            className="w-20 px-2 py-1.5 rounded-lg border border-indigo-300 text-sm font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                            aria-label={`Remaining seats for ${course.name}`}
                          />
                          <button
                            type="button"
                            disabled={savingSeats}
                            onClick={() => saveRemainingSeats(course)}
                            className="p-2 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                            aria-label="Save remaining seats"
                          >
                            <Check size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingCourseId('')}
                            className="p-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200"
                            aria-label="Cancel seat editing"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1.5 rounded-full text-xs font-black ${
                            course.remainingSeats === 0 ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'
                          }`}>
                            {course.remainingSeats === 0 ? 'Seats filled' : course.remainingSeats}
                          </span>
                          {canEditSeats && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setEditingCourseId(course.id);
                                setRemainingSeatsInput(String(course.remainingSeats));
                              }}
                              className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50"
                              aria-label={`Edit remaining seats for ${course.name}`}
                            >
                              <Pencil size={15} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-indigo-500"><ChevronRight size={20} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-slate-500">
            Showing {courses.length === 0 ? 0 : ((pagination.page - 1) * pagination.limit) + 1}
            {'–'}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} courses
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={tableLoading || pagination.page <= 1}
              onClick={() => setPagination((current) => ({ ...current, page: current.page - 1 }))}
              className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-sm font-bold text-slate-600">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              type="button"
              disabled={tableLoading || pagination.page >= pagination.totalPages}
              onClick={() => setPagination((current) => ({ ...current, page: current.page + 1 }))}
              className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
